import React, { useEffect, useMemo, useState } from "react";

/**
 * Floating summer particles — minimal version.
 * Soft floral + sea-side icons drift gently from top.
 * Vanishes completely 5 seconds after page load for a subtle welcome moment.
 */

const Flower = ({ color = "#E8A0B4" }) => (
  <svg viewBox="0 0 24 24" fill="none" width="100%" height="100%">
    <g fill={color}>
      <circle cx="12" cy="6" r="3.2" />
      <circle cx="18" cy="12" r="3.2" />
      <circle cx="12" cy="18" r="3.2" />
      <circle cx="6" cy="12" r="3.2" />
      <circle cx="12" cy="12" r="2.4" fill="#C5985A" />
    </g>
  </svg>
);

const Heart = ({ color = "#D4537E" }) => (
  <svg viewBox="0 0 24 24" fill="none" width="100%" height="100%">
    <path
      d="M12 21s-7-4.35-9.33-9.06C1.05 8.6 2.5 5 6 5c2.04 0 3.2 1 4 2 .8-1 1.96-2 4-2 3.5 0 4.95 3.6 3.33 6.94C19 16.65 12 21 12 21z"
      fill={color}
    />
  </svg>
);

const Sparkle = ({ color = "#C5985A" }) => (
  <svg viewBox="0 0 24 24" fill="none" width="100%" height="100%">
    <path
      d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"
      fill={color}
    />
  </svg>
);

const Seashell = () => (
  <svg viewBox="0 0 64 64" fill="none" width="100%" height="100%">
    <defs>
      <linearGradient id="shellGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F9D5C8" />
        <stop offset="1" stopColor="#E8A0B4" />
      </linearGradient>
    </defs>
    <path
      d="M32 8 C18 8 8 22 8 38 C8 52 22 58 32 58 C42 58 56 52 56 38 C56 22 46 8 32 8 Z"
      fill="url(#shellGrad)"
      stroke="#C5985A"
      strokeWidth="1.2"
    />
    {[12, 22, 32, 42, 52].map((x) => (
      <path key={x} d={`M32 12 L${x} 56`} stroke="#C5985A" strokeWidth="1" fill="none" opacity="0.55" />
    ))}
    <circle cx="32" cy="13" r="3" fill="#C5985A" opacity="0.7" />
  </svg>
);

const Conch = () => (
  <svg viewBox="0 0 64 64" fill="none" width="100%" height="100%">
    <defs>
      <linearGradient id="conchGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FBEAF0" />
        <stop offset="1" stopColor="#C5985A" />
      </linearGradient>
    </defs>
    <path
      d="M14 50 C 14 24 32 8 48 14 C 56 18 58 28 50 34 C 46 38 44 40 46 46 C 47 50 44 54 38 54 L 18 54 C 14 54 12 52 14 50 Z"
      fill="url(#conchGrad)"
      stroke="#C5985A"
      strokeWidth="1.2"
    />
    <path d="M22 50 C 26 38 36 28 44 24" stroke="#D4537E" strokeWidth="1.2" fill="none" opacity="0.5" />
  </svg>
);

const Starfish = () => (
  <svg viewBox="0 0 64 64" fill="none" width="100%" height="100%">
    <path
      d="M32 6 L40 24 L60 26 L44 38 L50 58 L32 48 L14 58 L20 38 L4 26 L24 24 Z"
      fill="#E89B6E"
      stroke="#C5985A"
      strokeWidth="1.2"
    />
  </svg>
);

const Jellyfish = () => (
  <svg viewBox="0 0 64 76" fill="none" width="100%" height="100%">
    <defs>
      <linearGradient id="jellyGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#E8A0B4" />
        <stop offset="1" stopColor="#D4537E" />
      </linearGradient>
    </defs>
    <path d="M8 32 C 8 14 24 6 32 6 C 40 6 56 14 56 32 L 56 38 C 56 40 54 42 52 42 L 12 42 C 10 42 8 40 8 38 Z" fill="url(#jellyGrad)" />
    <ellipse cx="22" cy="20" rx="6" ry="3" fill="#FBEAF0" opacity="0.6" />
    <path d="M20 42 Q 18 56 22 70" stroke="#D4537E" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M32 42 Q 32 58 30 74" stroke="#D4537E" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M44 42 Q 46 56 42 70" stroke="#D4537E" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

const ICON_TYPES = [
  { id: "flower-pink", render: () => <Flower color="#E8A0B4" />, size: [22, 32] },
  { id: "heart-blush", render: () => <Heart color="#D4537E" />, size: [16, 22] },
  { id: "sparkle", render: () => <Sparkle color="#C5985A" />, size: [12, 18] },
  { id: "seashell", render: () => <Seashell />, size: [22, 32] },
  { id: "conch", render: () => <Conch />, size: [24, 34] },
  { id: "starfish", render: () => <Starfish />, size: [20, 28] },
  { id: "jellyfish", render: () => <Jellyfish />, size: [22, 32] },
];

const FloatingSummer = ({ count = 10 }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Vanish after 5 seconds
    const t = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(t);
  }, []);

  const particles = useMemo(() => {
    const list = [];
    for (let i = 0; i < count; i++) {
      const type = ICON_TYPES[i % ICON_TYPES.length];
      const [minSize, maxSize] = type.size;
      const size = Math.round(minSize + Math.random() * (maxSize - minSize));
      const left = Math.random() * 100;
      const delay = Math.random() * 2;
      const duration = 5 + Math.random() * 3; // 5-8s
      const drift = (Math.random() - 0.5) * 50;
      const opacity = 0.5 + Math.random() * 0.2;
      const rotateEnd = (Math.random() - 0.5) * 360;
      list.push({
        ...type,
        size,
        left,
        delay,
        duration,
        drift,
        opacity,
        rotateEnd,
        key: `${type.id}-${i}`,
      });
    }
    return list;
  }, [count]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 overflow-hidden z-[5]"
      data-testid="floating-summer"
      style={{
        animation: "summerFadeOut 0.8s ease-out 4.2s forwards",
      }}
    >
      <style>{`
        @keyframes floatDown {
          0% { transform: translate3d(0, -12vh, 0) rotate(0deg); opacity: 0; }
          12% { opacity: 1; }
          88% { opacity: 1; }
          100% { transform: translate3d(var(--drift, 0), 112vh, 0) rotate(var(--rot, 180deg)); opacity: 0; }
        }
        @keyframes summerFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .gg-float {
          position: absolute;
          top: 0;
          animation-name: floatDown;
          animation-timing-function: linear;
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
      `}</style>
      {particles.map((p) => (
        <span
          key={p.key}
          className="gg-float"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--drift": `${p.drift}px`,
            "--rot": `${p.rotateEnd}deg`,
          }}
        >
          {p.render(p.size)}
        </span>
      ))}
    </div>
  );
};

export default FloatingSummer;
